import {toast, useMediaQuery} from '@cashiaApp/web-components';
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import PhoneInput, {isValidPhoneNumber, Value} from 'react-phone-number-input';

import 'react-phone-number-input/style.css';
import {
  CheckoutResponse,
  fetchQRCode,
  fetchQRCodeStatus,
  QRRequest,
  QRResponse,
  QRStatusRequest,
} from '../api';

export type CashiaInfo = {
  stikaOrPhone: Value;
};

// Export the ref methods so parent components can access validation
export interface CashiaWalletFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  getErrors: () => Partial<Record<keyof CashiaInfo, string>>;
}

let qrFetchedOnce = false;
let cachedQrCode: QRResponse | null = null;

const CashiaWalletForm = forwardRef<
  CashiaWalletFormRef,
  {
    cashiaInfo: CashiaInfo;
    setCashiaInfo: React.Dispatch<React.SetStateAction<CashiaInfo>>;
    reference: string;
    checkoutData: CheckoutResponse;
    onStatusChange?: (status: string) => void;
  }
>((props, ref) => {
  const {cashiaInfo, setCashiaInfo, reference, checkoutData, onStatusChange} =
    props;
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Initialize with cached value to prevent flickering
  const [qrCode, setQrCode] = useState<QRResponse | null>(cachedQrCode);
  const [qrLoading, setQrLoading] = useState(!cachedQrCode);
  const [qrError, setQrError] = useState<string | null>(null);
  // Separate state for image load error (don't affect QR data)
  const [imageError, setImageError] = useState(false);

  // Internal error state
  const [errors, setErrors] = useState<
    Partial<Record<keyof CashiaInfo, string>>
  >({});

  // Polling-related refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const qrCodeRef = useRef<QRResponse | null>(qrCode);

  // Keep ref in sync with qrCode state, but don't clear it once set
  useEffect(() => {
    if (qrCode) {
      qrCodeRef.current = qrCode;
    }
  }, [qrCode]);

  // Expose validation methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAll: () => {
      const newErrors: Partial<Record<keyof CashiaInfo, string>> = {};
      if (!cashiaInfo.stikaOrPhone) {
        newErrors.stikaOrPhone = 'Phone number is required';
      } else {
        if (!isValidPhoneNumber(cashiaInfo.stikaOrPhone)) {
          newErrors.stikaOrPhone = 'Please enter a valid phone number';
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    hasErrors: () => {
      return Object.values(errors).some((error) => error !== '');
    },
    getErrors: () => errors,
  }));

  // Real-time validation for phone number
  const validatePhoneNumber = (value: Value) => {
    if (!value) {
      setErrors((prev) => ({...prev, stikaOrPhone: ''}));
      return;
    }
    if (!isValidPhoneNumber(value)) {
      setErrors((prev) => ({
        ...prev,
        stikaOrPhone: 'Please enter a valid phone number',
      }));
      return;
    } else {
      setErrors((prev) => ({...prev, stikaOrPhone: ''}));
    }
  };

  const handlePhoneNumberChange = (value: Value) => {
    setCashiaInfo({...cashiaInfo, stikaOrPhone: value});
    validatePhoneNumber(value);
  };

  // Fetch QR code once
  useEffect(() => {
    if (isDesktop === undefined || !isDesktop) return; // ← wait until mediaQuery resolves & only fetch on desktop
    if (qrFetchedOnce) return;

    qrFetchedOnce = true;

    // Only show loading if we don't have cached data
    if (!cachedQrCode) {
      setQrLoading(true);
    }

    const load = async () => {
      try {
        const body: QRRequest = {description: reference || ''};
        const response = await fetchQRCode(reference, body);
        cachedQrCode = response;
        setQrCode(response);
        setQrError(null);
      } catch (err) {
        setQrError('Failed to load Cashia Pay QR.');
        setQrCode(null);
      } finally {
        setQrLoading(false);
      }
    };

    void load();
  }, [reference, isDesktop]);

  // Enhanced polling effect - starts when QR code is available
  useEffect(() => {
    // Don't start if no QR code yet
    if (!qrCode?.paymentIdentifier) {
      return;
    }
    // Don't start if already polling
    if (pollingIntervalRef.current) {
      return;
    }
    const fetchData = async () => {
      const currentQrCode = qrCodeRef.current;
      if (!currentQrCode?.paymentIdentifier) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }
      try {
        const requestBody: QRStatusRequest = {
          paymentIdentifier: currentQrCode.paymentIdentifier,
        };
        const response = await fetchQRCodeStatus(reference, requestBody);
        // Check if transaction is still pending
        if (response?.status === 'pending') {
          // Still pending, continue polling
          return;
        }
        // Transaction completed (success, failed, etc.)
        // Clear the polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        // Trigger payment completion handler
        handlePaymentCompletion(
          response?.status == 'paid',
          reference,
          response?.status || ''
        );
      } catch (error) {
        // // Clear the polling interval on error
        // if (pollingIntervalRef.current) {
        //   clearInterval(pollingIntervalRef.current);
        //   pollingIntervalRef.current = null;
        // }
      }
    };

    // Fetch immediately on start
    void fetchData();
    // Set up interval polling
    pollingIntervalRef.current = setInterval(() => {
      void fetchData();
    }, 2000);

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, reference]);

  const handlePaymentCompletion = useCallback(
    (isSuccess: boolean, requestId: string, status: string) => {
      if (onStatusChange) {
        onStatusChange(isSuccess ? 'success' : 'failed');
      }
      const baseUrl = isSuccess
        ? checkoutData?.successCallbackUrl
        : checkoutData?.errorCallbackUrl;
      if (baseUrl) {
        const url = `${baseUrl}?requestId=${requestId}&status=${status}`;
        toast.warning('Redirecting to website, please wait...');
        handleRedirect(url);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkoutData?.successCallbackUrl, checkoutData?.errorCallbackUrl]
  );

  const handleRedirect = (url: string) => {
    setTimeout(() => {
      window.location.href = `${url || ''}`;
    }, 5000);
  };

  const QRCodeDisplay = () => {
    // Always render the same container structure to prevent layout shifts
    return (
      <div className="flex flex-col items-center justify-center w-full">
        <div className="w-64 h-64 border-2 border-pink rounded-lg flex items-center justify-center overflow-hidden bg-white">
          {qrLoading ? (
            <p className="text-sm text-textGrey">Loading QR…</p>
          ) : qrError ? (
            <div className="w-full h-full bg-surfaceNegative border-pinkNegative flex items-center justify-center">
              <p className="text-sm text-brightRed px-4 text-center">
                {qrError}
              </p>
            </div>
          ) : imageError ? (
            <div className="w-full h-full bg-surfaceNegative border-pinkNegative flex items-center justify-center">
              <p className="text-sm text-brightRed px-4 text-center">
                QR code image failed to load
              </p>
            </div>
          ) : qrCode ? (
            <img
              src={qrCode.qrCodeMediaFileLink}
              alt="Cashia Pay QR"
              className="w-full h-full object-contain"
              onError={() => {
                // Handle 404 or failed image loads - don't clear qrCode data
                setImageError(true);
              }}
            />
          ) : null}
        </div>
        <p className="mt-3 text-base text-smoothRed font-semibold">
          Scan with Cashia App
        </p>
      </div>
    );
  };

  const MemoizedQRCodeDisplay = useMemo(() => {
    return <QRCodeDisplay />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, qrLoading, qrError, imageError]);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 relative">
        <div className={isDesktop ? 'block' : 'hidden'}>
          {MemoizedQRCodeDisplay}
        </div>
        {!isDesktop && (
          <>
            <div className="relative my-4">
              <div className="absolute top-1/2 h-[1px] bg-dividerGrey -translate-y-1/2 -left-3 -right-3 lg:-left-4 lg:-right-4" />
              <div className="relative flex items-center justify-center">
                <p className="text-sm text-textGrey whitespace-nowrap bg-white px-3">
                  send payment request
                </p>
              </div>
            </div>

            <div className="w-full">
              <label className="text-sm font-semibold text-borderGreyHover mb-2 block">
                Phone Number
              </label>

              <div className="w-full border rounded-md border-subtleGrey">
                <PhoneInput
                  defaultCountry="KE"
                  countries={['KE']}
                  placeholder="Enter Phone Number"
                  value={cashiaInfo.stikaOrPhone}
                  onChange={handlePhoneNumberChange}
                  className={`
                      ${
                        errors.stikaOrPhone
                          ? 'text-smoothRed border-pinkNegative border w-full rounded-md bg-surfaceNegative px-4 py-4'
                          : 'w-full border-none rounded-md px-4 py-4 text-headlineGrayscale focus:outline-none focus:ring-none focus:border-none bg-transparent'
                      }
                    `}
                  style={{
                    '--PhoneInputCountryFlag-height': '1em',
                    '--PhoneInputCountrySelectArrow-color': '#6b7280',
                    '--PhoneInput--focus': 'transparent',
                    '--PhoneInput-border-color--focus': 'transparent',
                    '--PhoneInput-outline': 'none',
                    '--PhoneInputInput-outline': 'none',
                    '--PhoneInputCountrySelect-outline': 'none',
                  }}
                />
              </div>
              {errors.stikaOrPhone && (
                <div className="mt-1 mb-2 transition-all duration-300 ease-in-out">
                  <p className="text-brightRed text-sm font-normal animate-fade-in">
                    {errors.stikaOrPhone}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

CashiaWalletForm.displayName = 'CashiaWalletForm';

export default CashiaWalletForm;
